import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { api } from 'api/v1/api';
import { addNotification } from 'notifications';
import { AUTH_SESSION_QUERY_KEY } from 'hooks/useAuthSession';
import { Button, CompactText, Notice, TextField } from 'ui';

import { chatKeys } from '../api/queries';
import classes from '../Chat.module.scss';
import type { ContactShareState } from '../types';

interface ShareContactsButtonProps {
  contactShare: ContactShareState | null;
  chatId: number | null;
  userId: number | null;
  initialPhone?: string;
  initialEmail?: string;
  onConsent: () => Promise<void>;
  onContactsUpdated?: () => void;
  disabled?: boolean;
}

export const ShareContactsButton: React.FC<ShareContactsButtonProps> = ({
  contactShare,
  chatId,
  userId,
  initialPhone = '',
  initialEmail = '',
  onConsent,
  onContactsUpdated,
  disabled = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [highlightForm, setHighlightForm] = useState(false);
  const [phone, setPhone] = useState(initialPhone);
  const [email, setEmail] = useState(initialEmail);
  const [formError, setFormError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const revealFormRef = useRef(false);
  const queryClient = useQueryClient();
  const storageKey = chatId ? `chat-notice:${chatId}:contacts` : null;

  useEffect(() => {
    setPhone(initialPhone);
    setEmail(initialEmail);
  }, [initialPhone, initialEmail]);

  useEffect(() => {
    if (!storageKey) return;
    setCollapsed(sessionStorage.getItem(storageKey) === '1');
  }, [storageKey]);

  useEffect(() => {
    if (collapsed || !revealFormRef.current) return;
    revealFormRef.current = false;
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    setHighlightForm(true);
    const timer = window.setTimeout(() => setHighlightForm(false), 2000);
    return () => window.clearTimeout(timer);
  }, [collapsed]);

  const setFold = (next: boolean) => {
    setCollapsed(next);
    if (storageKey) sessionStorage.setItem(storageKey, next ? '1' : '0');
  };

  if (!contactShare?.eligible || contactShare.shared) {
    return null;
  }

  const needsContacts = contactShare.user_has_contacts === false;
  const missing = contactShare.missing_fields ?? [];
  const showPhoneField = missing.includes('phone');
  const showEmailField = missing.includes('email');

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    setHighlightForm(true);
    window.setTimeout(() => setHighlightForm(false), 2000);
  };

  const handleShareClick = async () => {
    if (contactShare.user_consented || disabled || isLoading) {
      return;
    }
    if (needsContacts) {
      addNotification(
        'Укажите телефон и email - без них нельзя поделиться контактами с собеседником.',
        'warning',
      );
      if (collapsed) {
        revealFormRef.current = true;
        setFold(false);
        return;
      }
      scrollToForm();
      return;
    }
    setIsLoading(true);
    try {
      await onConsent();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveContacts = async (shareAfterSave: boolean) => {
    if (!userId) {
      setFormError('Не удалось определить пользователя');
      return;
    }
    if (showPhoneField && phone.replace(/\D/g, '').length < 11) {
      setFormError('Укажите номер телефона полностью');
      return;
    }
    if (showEmailField && !email.trim()) {
      setFormError('Укажите email');
      return;
    }

    setFormError(null);
    setIsLoading(true);
    try {
      const payload: Record<string, string> = {};
      if (showPhoneField) {
        payload.phone = phone.trim();
      }
      if (showEmailField) {
        payload.email = email.trim();
      }
      await api.put(`/users/${userId}`, payload);
      if (chatId) {
        await queryClient.refetchQueries({ queryKey: chatKeys.contactShare(chatId) });
      }
      await queryClient.invalidateQueries({ queryKey: AUTH_SESSION_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['profile'] });
      onContactsUpdated?.();
      addNotification('Контакты сохранены', 'success');
      if (shareAfterSave) {
        await onConsent();
      }
    } catch {
      setFormError('Не удалось сохранить контакты. Проверьте телефон и email.');
    } finally {
      setIsLoading(false);
    }
  };

  const foldButton = (label: 'Скрыть' | 'Показать') => (
    <button
      type='button'
      className={classes['share-contacts-fold']}
      aria-expanded={label === 'Скрыть'}
      onClick={() => setFold(label !== 'Показать')}
    >
      {label}
    </button>
  );

  if (collapsed) {
    return (
      <div
        className={clsx(
          classes['share-contacts-folded'],
          needsContacts && !contactShare.user_consented && classes['share-contacts-folded--warning'],
        )}
      >
        <p>
          {contactShare.user_consented
            ? contactShare.other_consented
              ? 'Контакты переданы'
              : 'Ожидаем согласие собеседника'
            : 'Обмен контактами'}
        </p>
        {foldButton('Показать')}
      </div>
    );
  }

  if (contactShare.user_consented) {
    return (
      <Notice
        tone={contactShare.other_consented ? 'success' : 'info'}
        action={foldButton('Скрыть')}
      >
        {contactShare.other_consented
          ? 'Контакты переданы'
          : 'Вы согласились поделиться контактами. Ожидаем согласие собеседника.'}
      </Notice>
    );
  }

  return (
    <>
      <Notice
        className={classes['share-contacts-notice']}
        tone={needsContacts ? 'warning' : 'info'}
        action={
          <>
            <button
              type='button'
              className={classes['share-contacts-button']}
              onClick={() => void handleShareClick()}
              disabled={disabled || isLoading}
            >
              {isLoading ? 'Отправка...' : 'Поделиться контактами'}
            </button>
            {foldButton('Скрыть')}
          </>
        }
      >
        {needsContacts
          ? 'Перед обменом контактами укажите телефон и email.'
          : 'Вы договорились по заявке - подтвердите обмен контактами, чтобы договориться о занятиях.'}
      </Notice>

      {needsContacts && (
        <div
          ref={formRef}
          className={clsx(
            classes['share-contacts-form'],
            highlightForm && classes['share-contacts-form--highlight'],
          )}
        >
          <CompactText className={classes['share-contacts-form-hint']} tone='info'>
            Заполните недостающие контакты - они будут переданы собеседнику после взаимного согласия.
          </CompactText>
          {showPhoneField && (
            <TextField
              shape='brick'
              label='Номер телефона'
              placeholder='Номер телефона'
              type='text'
              mask='ru-phone'
              size='md'
              value={phone}
              onChange={setPhone}
            />
          )}
          {showEmailField && (
            <TextField
              shape='brick'
              label='Электронная почта'
              placeholder='email@example.com'
              size='md'
              value={email}
              onChange={setEmail}
            />
          )}
          {formError && <p className={classes['share-contacts-error']}>{formError}</p>}
          <div className={classes['share-contacts-form-actions']}>
            <Button
              className={classes['share-contacts-form-btn']}
              variant='filled'
              size='sm'
              shape='brick'
              disabled={isLoading}
              onClick={() => void handleSaveContacts(false)}
            >
              {isLoading ? 'Сохранение...' : 'Сохранить'}
            </Button>
            <Button
              className={classes['share-contacts-form-btn']}
              variant='filled'
              size='sm'
              shape='brick'
              color='secondary'
              disabled={isLoading}
              onClick={() => void handleSaveContacts(true)}
            >
              {isLoading ? 'Сохранение...' : 'Сохранить и поделиться'}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
